import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import * as React from 'react'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { ParticipantsFilterComponent } from '@/scopes/search/components/ParticipantsFilterComponent'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { createSearchFilterClickEvent } from '@/scopes/search/search/domain/analytics'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'
import { useValueFilter } from '@/scopes/search/search/domain/hooks'
import { getSortingAccordingToTestAndPartyComposition, POPULARITY_SORT_VARIANTS } from '@/scopes/search/search/domain/sorting'
import { isFlightOnlySelector } from '@/scopes/search/search/infrastructure/store/selectors'
import { useSearchStore, useSearchStoreSelector } from '@/scopes/search/search/infrastructure/store/useSearchStore'
import { QuickSearchFilterProps } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/types'

function ParticipantsQuickSearchItemFilter({ id, asSheet }: QuickSearchFilterProps) {
  const { setActiveItem } = useQuickSearchStore()
  const { sendEvent } = useAnalytics()
  const { isFetching, setSelection } = useValueFilter('Participants')
  const isFlightOnly = useSearchStoreSelector(isFlightOnlySelector)
  const { prioritySorting } = useOptimizelyFlagContext()
  const { sorting, filterSelection, setParticipantsAndSorting } = useSearchStore()

  function handleConfirm(roomsParticipantDistribution: RoomsParticipantDistribution) {
    setActiveItem(null)

    if (prioritySorting && POPULARITY_SORT_VARIANTS.has(sorting.by)) {
      const newSorting = getSortingAccordingToTestAndPartyComposition(
        { sorting, filterSelection: { ...filterSelection, Participants: roomsParticipantDistribution } },
        prioritySorting,
      )
      setParticipantsAndSorting(roomsParticipantDistribution, newSorting)
    }
    else {
      setSelection(roomsParticipantDistribution)
    }

    sendEvent(
      createSearchFilterClickEvent({
        element: 'participants selector',
        type: 'partyComposition',
        result: formatParticipantDistributionToGA4Result(roomsParticipantDistribution).distribution,
      }),
    )
  }

  return (
    <ParticipantsFilterComponent
      setSelection={handleConfirm}
      disabled={isFetching}
      id={id}
      asSheet={asSheet}
      isMultiRoom={!isFlightOnly}
    />
  )
}

export { ParticipantsQuickSearchItemFilter }
